import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import SEO from '../../components/seo/SEO';
import SchemaMarkup from '../../components/seo/SchemaMarkup';
import faqService from '../../services/faq';
import type { FaqCategoryWithQuestions } from '../../types/faq';
import { useLanguageLink } from '../../hooks/useLanguageLink';

export default function PublicFaqPage() {
  const { createLink } = useLanguageLink();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  // Fetch FAQ data from database
  const { data, isLoading } = useQuery({
    queryKey: ['public-faq'],
    queryFn: () => faqService.getPublicFaqs(),
  });

  // Filter out categories that should only appear in the dashboard
  const excludedSlugs = ['campaign-setup-guide', 'integration'];
  const categories = (data?.categories || []).filter(
    (cat) => !excludedSlugs.includes(cat.slug)
  );

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <SEO
        title="FAQ - Traffic Filtering Questions Answered"
        description="Find answers to frequently asked questions about Axetra traffic filtering platform. Learn about bot detection, pricing, setup, integrations, and more."
        canonical="/faq"
        keywords="traffic filtering faq, bot detection questions, ad protection help, axetra support"
      />

      {/* Schema Markup */}
      {!isLoading && categories.length > 0 && (
        <SchemaMarkup
          type="faq"
          questions={categories.flatMap(category =>
            category.questions.map(q => ({ question: q.question, answer: q.answer }))
          )}
        />
      )}

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-primary-100 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl opacity-30" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30" />
        <div className="container-marketing relative">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
                Frequently Asked Questions
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Everything you need to know about Axetra. Can't find what you're looking for? Contact our support team.
            </p>
            <Link
              to={createLink('register')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-800 text-white rounded-lg font-semibold hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-white">
        <div className="container-marketing max-w-4xl">
          {isLoading ? (
            // Loading skeleton
            <div className="space-y-12">
              {[...Array(3)].map((_, categoryIndex) => (
                <div key={categoryIndex}>
                  <div className="h-8 w-48 bg-gray-200 rounded mb-6 animate-pulse"></div>
                  <div className="space-y-4">
                    {[...Array(3)].map((_, questionIndex) => (
                      <div key={questionIndex} className="bg-gray-50 rounded-xl p-6 border border-gray-200 animate-pulse">
                        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            categories.map((category, categoryIndex) => {
              // Calculate global index for each question across all categories
              let questionOffset = 0;
              for (let i = 0; i < categoryIndex; i++) {
                questionOffset += categories[i].questions.length;
              }

              return (
                <div key={category.id} className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <span className="w-1 h-8 bg-gradient-to-b from-primary-600 to-purple-600 rounded-full"></span>
                    {category.name}
                  </h2>
                  <div className="space-y-4">
                    {category.questions.map((question, questionIndex) => {
                      const globalIndex = questionOffset + questionIndex;
                      const isOpen = openIndex === globalIndex;

                      return (
                        <div
                          key={question.id}
                          className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200 hover:border-primary-300 transition-all duration-200"
                        >
                          <button
                            onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                          >
                            <span className="font-semibold text-gray-900 pr-4">{question.question}</span>
                            <svg
                              className={`w-5 h-5 text-primary-600 flex-shrink-0 transition-transform duration-200 ${
                                isOpen ? 'rotate-180' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {isOpen && (
                            <div className="px-6 pb-4">
                              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{question.answer}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-20 bg-gray-50">
        <div className="container-marketing text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Still have questions?</h2>
          <p className="text-lg text-gray-600 mb-8">
            Our support team is here to help. Reach out and we'll get back to you within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="#"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-600 border-2 border-primary-200 rounded-lg font-semibold hover:border-primary-400 hover:bg-primary-50 transition-all duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Contact Support
            </Link>
            <Link
              to={createLink('register')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
